/** @format */

import {
  AuditActionType,
  AuditActionStatus,
  AuditLogEntry,
  AuditLogSearchParams,
  AuditLogResponse,
  AuditLogExportParams,
  AuditSummaryStats,
  UserAuditTrail,
  ResourceType,
  DeviceInfo,
  LocationData,
  RiskLevel,
} from '../types/auditLog';
import { getValidAccessToken } from '@utils/authenticatedFetch';
import { getApiOrigin } from '@utils/apiOrigin';
import {
  AUDIT_DEBUG_MODE,
  ACTION_RISK_LEVELS,
  SESSION_ID_KEY,
  AUDIT_LOG_BATCH_SIZE,
  AUDIT_LOG_BATCH_TIMEOUT,
  AUDIT_LOGS_API_URL,
} from '@constants/auditConstants';
import { shouldSkipClientIpApiFetch } from '@utils/ipGeolocation';
import { randomId } from '@utils/randomId';

/**
 * HIPAA-Compliant Audit Logging Service
 *
 * This service handles all audit logging for the application according to
 * HIPAA Security Rule 45 C.F.R. § 164.312(b) requirements.
 *
 * Features:
 * - Automatic device and location detection
 * - Batch logging for performance
 * - Risk level calculation
 * - Session tracking
 * - Placeholder backend integration (ready for real API)
 */

export type ApiCallAuditLogPayload = {
  user_id: number;
  endpoint: string;
  method: string;
  request_payload?: Record<string, unknown>;
  response_payload?: Record<string, unknown>;
  status_code?: number;
  error_message?: string | null;
  duration_ms?: number;
  facility_info?: Record<string, unknown>;
  before_value?: Record<string, unknown> | null;
  after_value?: Record<string, unknown> | null;
  action_details?: Record<string, unknown>;
  resource_type?: string;
  action_type?: string;
  action_status?: string;
};

class AuditLogService {
  private sessionId: string;
  private locationData: LocationData | null = null;
  private deviceInfo: DeviceInfo;
  private logQueue: AuditLogEntry[] = [];
  private batchTimer: NodeJS.Timeout | null = null;
  private apiCallQueue: ApiCallAuditLogPayload[] = [];
  private apiCallBatchTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.sessionId = this.initializeSessionId();
    this.deviceInfo = this.detectDevice();
    this.initializeLocationData();
  }

  /**
   * Initialize or retrieve session ID
   */
  private initializeSessionId(): string {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = randomId();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  /**
   * Detect device information from user agent
   */
  private detectDevice(): DeviceInfo {
    const ua = navigator.userAgent;

    // Basic browser detection
    let browser = 'Unknown';
    let browserVersion = 'Unknown';

    if (ua.indexOf('Firefox') > -1) {
      browser = 'Firefox';
      browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (ua.indexOf('Chrome') > -1) {
      browser = 'Chrome';
      browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (ua.indexOf('Safari') > -1) {
      browser = 'Safari';
      browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (ua.indexOf('Edge') > -1) {
      browser = 'Edge';
      browserVersion = ua.match(/Edge\/(\d+\.\d+)/)?.[1] || 'Unknown';
    }

    // OS detection
    let os = 'Unknown';
    let osVersion = 'Unknown';

    if (ua.indexOf('Windows NT 10.0') > -1) {
      os = 'Windows';
      osVersion = '10';
    } else if (ua.indexOf('Windows NT 6.3') > -1) {
      os = 'Windows';
      osVersion = '8.1';
    } else if (ua.indexOf('Windows NT 6.2') > -1) {
      os = 'Windows';
      osVersion = '8';
    } else if (ua.indexOf('Mac OS X') > -1) {
      os = 'macOS';
      osVersion = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || 'Unknown';
    } else if (ua.indexOf('Linux') > -1) {
      os = 'Linux';
    } else if (ua.indexOf('Android') > -1) {
      os = 'Android';
      osVersion = ua.match(/Android (\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1) {
      os = 'iOS';
      osVersion = ua.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown';
    }

    // Device type detection
    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (/Mobi|Android/i.test(ua)) {
      deviceType = 'mobile';
    } else if (/Tablet|iPad/i.test(ua)) {
      deviceType = 'tablet';
    }

    return {
      userAgent: ua,
      browser,
      browserVersion,
      os,
      osVersion,
      deviceType,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
    };
  }

  /**
   * Initialize location data (async)
   */
  private async initializeLocationData(): Promise<void> {
    if (shouldSkipClientIpApiFetch()) {
      this.locationData = { ipAddress: 'Unknown' };
      if (AUDIT_DEBUG_MODE) {
        console.log(
          'ℹ️ Audit: Skipping ipapi.co on local dev (avoids CORS / rate limits)',
        );
      }
      return;
    }
    try {
      // Try to get location from Application Insights context first
      // Or fetch from ipapi.co
      const response = await fetch('https://ipapi.co/json/');
      if (response.ok) {
        const data = await response.json();
        this.locationData = {
          ipAddress: data.ip,
          city: data.city,
          region: data.region,
          country: data.country_name,
          countryCode: data.country_code,
          latitude: data.latitude,
          longitude: data.longitude,
          timezone: data.timezone,
          org: data.org,
        };

        if (AUDIT_DEBUG_MODE) {
          console.log('✅ Audit: Location data initialized', this.locationData);
        }
      } else {
        this.locationData = { ipAddress: 'Unknown' };
      }
    } catch (error) {
      // Silently handle location fetch failures - this is expected in CI/test environments
      // where ipapi.co may be blocked by CORS or network policies
      if (AUDIT_DEBUG_MODE) {
        console.warn('⚠️ Audit: Location data unavailable (CORS or network issue)');
      }
      // Set default values
      this.locationData = {
        ipAddress: 'Unknown',
      };
    }
  }

  /**
   * Get current user information from localStorage
   */
  private getCurrentUser(): { userId?: number; userEmail?: string; userName?: string } {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return {
          userId: user.user_id || user.id,
          userEmail: user.email,
          userName: user.display_name || user.first_name || 'Unknown',
        };
      }
    } catch (error) {
      console.error('❌ Audit: Failed to get user info:', error);
    }
    return {};
  }

  /**
   * Calculate risk level for an action
   */
  private calculateRiskLevel(
    actionType: AuditActionType,
    resourceType: ResourceType,
    isAdminAction: boolean = false
  ): RiskLevel {
    // Get base risk level from action type
    let riskLevel = ACTION_RISK_LEVELS[actionType] || RiskLevel.LOW;

    // Elevate risk for admin actions
    if (isAdminAction) {
      if (riskLevel === RiskLevel.LOW) riskLevel = RiskLevel.MEDIUM;
      else if (riskLevel === RiskLevel.MEDIUM) riskLevel = RiskLevel.HIGH;
    }

    // Elevate risk for ePHI resources
    const ephiResources = [
      ResourceType.FACILITY,
      ResourceType.FACILITY_DETAILS,
      ResourceType.COMPLIANCE_DATA,
      ResourceType.USER_PROFILE,
    ];
    if (ephiResources.includes(resourceType)) {
      if (riskLevel === RiskLevel.LOW) riskLevel = RiskLevel.MEDIUM;
    }

    return riskLevel;
  }

  /**
   * Create a complete audit log entry
   */
  private createLogEntry(
    actionType: AuditActionType,
    resourceType: ResourceType,
    options: Partial<AuditLogEntry> = {}
  ): AuditLogEntry {
    const user = this.getCurrentUser();
    const timestamp = new Date().toISOString();

    // Calculate retention date (6 years from now)
    const retentionDate = new Date();
    retentionDate.setFullYear(retentionDate.getFullYear() + 6);

    const isAdminAction = options.isAdminAction || false;
    const riskLevel = this.calculateRiskLevel(actionType, resourceType, isAdminAction);

    return {
      timestamp,
      userId: user.userId,
      userEmail: user.userEmail,
      userName: user.userName,
      actionType,
      actionStatus: options.actionStatus || AuditActionStatus.SUCCESS,
      resourceType,
      sessionId: this.sessionId,
      deviceInfo: this.deviceInfo,
      locationData: this.locationData || undefined,
      applicationVersion: process.env.APP_VERSION || '1.0.0',
      riskLevel,
      retentionUntil: retentionDate.toISOString(),
      ...options,
    };
  }

  /**
   * Check if user is authenticated (has valid access token)
   */
  private isAuthenticated(): boolean {
    return getValidAccessToken() !== null;
  }

  /**
   * Send log entry to backend (placeholder implementation)
   */
  private async sendToBackend(logs: AuditLogEntry[]): Promise<void> {
    // Skip sending audit logs on public signup input form page
    // (user explicitly requested no /api/v1/audit/log calls on #/signup-request and campaign URL)
    if (typeof window !== 'undefined') {
      const path = window.location.pathname || '';
      if (
        path.includes('/signup-request') ||
        path.includes('/campaign-60-day-pilot-A') ||
        path.includes('/Microsoft-Supported-60-Day-Compliance-Pilot')
      ) {
        if (AUDIT_DEBUG_MODE) {
          console.log('⏭️ Audit: skipping log send on signup/campaign page');
        }
        return;
      }
    }

    if (AUDIT_DEBUG_MODE) {
      console.log('📝 Audit Logs (PLACEHOLDER - Not sent to backend):', logs);
      return;
    }

    // Don't attempt to send logs if user is not authenticated
    // This prevents 401 errors during login flow and for unauthenticated users
    if (!this.isAuthenticated()) {
      if (AUDIT_DEBUG_MODE) {
        console.log('⏸️ Audit logs queued (user not authenticated):', logs.length, 'logs');
      }
      // Re-queue logs for later when user is authenticated
      // Only re-queue non-authentication logs to avoid infinite loops
      const nonAuthLogs = logs.filter(log => log.resourceType !== ResourceType.AUTHENTICATION);
      if (nonAuthLogs.length > 0) {
        this.logQueue.unshift(...nonAuthLogs);
      }
      return;
    }

    try {
      // PLACEHOLDER: This will be replaced with actual backend API call
      const response = await fetch(AUDIT_LOGS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getValidAccessToken() || ''}`,
        },
        body: JSON.stringify({ logs }),
      });

      if (!response.ok) {
        // Handle known non-critical error codes silently
        // 401 = Token expired/invalid (user session issue, not application error)
        // 422 = Validation error (payload issue, log and continue)
        if (response.status === 401 || response.status === 422) {
          if (AUDIT_DEBUG_MODE) {
            console.warn(`⚠️ Audit log API returned ${response.status} - logs may not be saved`);
          }
          return; // Don't throw, just return silently
        }
        throw new Error(`Audit log API failed: ${response.status}`);
      }

      if (AUDIT_DEBUG_MODE) {
        console.log('✅ Audit logs sent to backend successfully');
      }
    } catch (error) {
      // IMPORTANT: Even if backend fails, we should not break the application
      // Only log unexpected errors (not network/CORS issues which are common in CI)
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      // Silently ignore common non-critical errors
      if (errorMessage.includes('401') || errorMessage.includes('422') || 
          errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        if (AUDIT_DEBUG_MODE) {
          console.warn('⚠️ Audit log send failed (non-critical):', errorMessage);
        }
        return;
      }
      
      // Log unexpected errors for debugging
      console.error('❌ Failed to send audit logs to backend:', error);
    }
  }

  /**
   * Add log to queue and process batch
   */
  private async queueLog(logEntry: AuditLogEntry): Promise<void> {
    this.logQueue.push(logEntry);

    // Send immediately for high/critical risk actions
    if (logEntry.riskLevel === RiskLevel.HIGH || logEntry.riskLevel === RiskLevel.CRITICAL) {
      await this.flushQueue();
      return;
    }

    // Send batch if queue is full
    if (this.logQueue.length >= AUDIT_LOG_BATCH_SIZE) {
      await this.flushQueue();
      return;
    }

    // Schedule batch send
    if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => {
        this.flushQueue();
      }, AUDIT_LOG_BATCH_TIMEOUT);
    }
  }

  /**
   * Flush the log queue to backend
   */
  private async flushQueue(): Promise<void> {
    if (this.logQueue.length === 0) return;

    const logsToSend = [...this.logQueue];
    this.logQueue = [];

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    await this.sendToBackend(logsToSend);
  }

  /**
   * PUBLIC API METHODS
   */

  /**
   * Log a generic action
   */
  public async log(
    actionType: AuditActionType,
    resourceType: ResourceType,
    options: Partial<AuditLogEntry> = {}
  ): Promise<void> {
    const logEntry = this.createLogEntry(actionType, resourceType, options);
    await this.queueLog(logEntry);
  }

  /**
   * Log authentication event
   */
  public async logAuthentication(
    action: 'login' | 'logout' | 'login_failed' | 'password_reset' | 'otp' | 'signup',
    method: 'MSAL' | 'Google' | 'Email' | 'OTP',
    details: {
      success: boolean;
      email?: string;
      reason?: string;
      deviceInfo?: Partial<DeviceInfo>;
    }
  ): Promise<void> {
    const actionTypeMap = {
      login: AuditActionType.LOGIN,
      logout: AuditActionType.LOGOUT,
      login_failed: AuditActionType.LOGIN_FAILED,
      password_reset: AuditActionType.PASSWORD_RESET,
      otp: AuditActionType.OTP_VERIFY,
      signup: AuditActionType.LOGIN, // Signup is treated as a login for audit purposes
    };

    await this.log(actionTypeMap[action], ResourceType.AUTHENTICATION, {
      authenticationMethod: method,
      actionStatus: details.success ? AuditActionStatus.SUCCESS : AuditActionStatus.FAILURE,
      actionDetails: {
        email: details.email,
        reason: details.reason,
      },
    });
  }

  /**
   * Log resource access
   */
  public async logResourceAccess(
    resourceType: ResourceType,
    resourceId: string | number,
    action: 'view' | 'edit' | 'delete' | 'export',
    details?: {
      resourceName?: string;
      beforeValue?: Record<string, any>;
      afterValue?: Record<string, any>;
      accessReason?: string;
    }
  ): Promise<void> {
    const actionTypeMap = {
      view: AuditActionType.VIEW,
      edit: AuditActionType.UPDATE,
      delete: AuditActionType.DELETE,
      // export: AuditActionType.EXPORT, // Removed - replaced with DOWNLOAD
      export: AuditActionType.DOWNLOAD,
    };

    await this.log(actionTypeMap[action], resourceType, {
      resourceId,
      resourceName: details?.resourceName,
      beforeValue: details?.beforeValue,
      afterValue: details?.afterValue,
      accessReason: details?.accessReason,
    });
  }

  /**
   * Log API call
   */
  public async logApiCall(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    path: string,
    statusCode: number,
    responseTime: number,
    resourceType: ResourceType,
    resourceId?: string | number
  ): Promise<void> {
    const actionType = method === 'GET' ? AuditActionType.READ :
                      method === 'POST' ? AuditActionType.CREATE :
                      method === 'PUT' || method === 'PATCH' ? AuditActionType.UPDATE :
                      AuditActionType.DELETE;

    const success = statusCode >= 200 && statusCode < 300;

    await this.log(actionType, resourceType, {
      actionStatus: success ? AuditActionStatus.SUCCESS : AuditActionStatus.FAILURE,
      requestMethod: method,
      requestPath: path,
      responseStatusCode: statusCode,
      responseTime,
      resourceId,
    });
  }

  /**
   * Log search operation
   */
  public async logSearch(
    resourceType: ResourceType,
    searchTerm: string,
    resultsCount: number,
    filters?: Record<string, any>
  ): Promise<void> {
    await this.log(AuditActionType.SEARCH, resourceType, {
      actionDetails: {
        searchTerm,
        resultsCount,
        filters,
      },
    });
  }

  /**
   * Build filters payload for audit log search
   */
  private buildFiltersPayload(params: AuditLogSearchParams): Record<string, any> {
    const filters: Record<string, any> = {};

    if (params.userId) filters.user_id = params.userId;
    if (params.userEmail) filters.user_email = params.userEmail;
    if (params.actionType) filters.action_type = params.actionType;
    if (params.resourceType) filters.resource_type = params.resourceType;
    if (params.actionStatus) filters.action_status = params.actionStatus;
    if (params.riskLevel) filters.risk_level = params.riskLevel;
    if (params.facilityId) filters.facility_id = params.facilityId;

    return filters;
  }

  /**
   * Remove pagination params so we can control them manually (e.g., export)
   */
  private sanitizeFilters(filters?: AuditLogSearchParams): AuditLogSearchParams {
    if (!filters) return {};
    const { page: _page, pageSize: _pageSize, ...rest } = filters;
    return { ...rest } as AuditLogSearchParams;
  }

  private csvEscape(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '""';
    }

    let serializedValue: string;
    if (typeof value === 'object') {
      try {
        serializedValue = JSON.stringify(value);
      } catch (error) {
        serializedValue = '';
      }
    } else {
      serializedValue = String(value);
    }

    return `"${serializedValue.replace(/"/g, '""')}"`;
  }

  private buildCsvFromLogs(logs: AuditLogEntry[]): string {
    const headers = [
      'Timestamp',
      'User Email',
      'User ID',
      'Action Type',
      'Action Status',
      'Resource Type',
      'Resource Name',
      'Resource ID',
      'Risk Level',
      'IP Address',
      'Location',
      'Device',
      'Is Admin Action',
      'Details',
    ];

    const rows = logs.map((log) => [
      log.timestamp,
      log.userEmail || '',
      log.userId ?? '',
      log.actionType,
      log.actionStatus,
      log.resourceType,
      log.resourceName || '',
      log.resourceId ?? '',
      log.riskLevel || '',
      log.locationData?.ipAddress || '',
      log.locationData?.city || '',
      log.deviceInfo ? `${log.deviceInfo.browser} / ${log.deviceInfo.os}` : '',
      log.isAdminAction ? 'true' : 'false',
      log.actionDetails ? JSON.stringify(log.actionDetails) : '',
    ]);

    const csvRows = rows.map((row) => row.map((value) => this.csvEscape(value)).join(','));
    return [headers.join(','), ...csvRows].join('\n');
  }

  /**
   * Normalize API response log into AuditLogEntry
   */
  private mapApiLogToEntry(apiLog: any): AuditLogEntry {
    const details = apiLog?.action_details ?? undefined;
    let rawAction = apiLog?.action_type;
    let rawResource = apiLog?.resource_type;

    // Fill gaps from action_details (page / action) when API columns empty
    if (!rawAction && details?.action) {
      rawAction = String(details.action).toUpperCase();
    }
    if (!rawResource && details?.page) {
      const page = String(details.page).trim();
      const pageMap: Record<string, ResourceType> = {
        'Facility Analysis': ResourceType.FACILITY_ANALYSIS,
        'Potential Penalties': ResourceType.POTENTIAL_PENALTIES,
        'Exclusion / Sanction Check': ResourceType.EXCLUSION_SANCTION_CHECK,
        'Continuous Compliance': ResourceType.CONTINUOUS_COMPLIANCE,
        'Risk Analysis': ResourceType.RISK_ANALYSIS,
        'Mock Survey': ResourceType.MOCK_SURVEY,
        'Survey Viewer from Mock survey screen': ResourceType.SURVEY_VIEWER,
        'User Management': ResourceType.USER_MANAGEMENT,
        Billings: ResourceType.BILLINGS,
        'Audit Logs': ResourceType.AUDIT_LOGS,
        'Authentication / Session': ResourceType.AUTHENTICATION_SESSION,
        'Discovery Content': ResourceType.DISCOVERY_CONTENT,
        Discover: ResourceType.DISCOVERY_CONTENT,
      };
      rawResource =
        pageMap[page] ||
        Object.entries(pageMap).find(([k]) => k.toLowerCase() === page.toLowerCase())?.[1];
    }

    const actionType =
      Object.values(AuditActionType).includes(rawAction) ?
        (rawAction as AuditActionType) :
        AuditActionType.VIEW;
    const actionStatus =
      Object.values(AuditActionStatus).includes(apiLog?.action_status) ?
        (apiLog.action_status as AuditActionStatus) :
        AuditActionStatus.SUCCESS;
    const resourceType =
      Object.values(ResourceType).includes(rawResource) ?
        (rawResource as ResourceType) :
        ResourceType.FACILITY_ANALYSIS;
    const riskLevel =
      Object.values(RiskLevel).includes(apiLog?.risk_level) ?
        (apiLog.risk_level as RiskLevel) :
        undefined;

    const timestamp = apiLog?.timestamp ? new Date(apiLog.timestamp).toISOString() : new Date().toISOString();

    const mergedActionDetails = {
      ...(apiLog?.action_details ?? {}),
      ...(apiLog?.facility_info ? { facility_info: apiLog.facility_info } : {}),
      ...(apiLog?.facility_name ? { facility_name: apiLog.facility_name } : {}),
    };

    return {
      id: apiLog?.id?.toString(),
      timestamp,
      userId: apiLog?.user_id ?? undefined,
      userEmail: apiLog?.user_email ?? undefined,
      userName:
        apiLog?.user_name ??
        apiLog?.username ??
        apiLog?.display_name ??
        apiLog?.action_details?.user_name ??
        apiLog?.action_details?.userName ??
        undefined,
      actionType,
      actionStatus,
      resourceType,
      resourceId: apiLog?.resource_id ?? undefined,
      resourceName: apiLog?.resource_name ?? (details?.page ? String(details.page) : undefined),
      beforeValue: apiLog?.before_value ?? undefined,
      afterValue: apiLog?.after_value ?? undefined,
      riskLevel,
      isAdminAction: apiLog?.is_admin_action ?? undefined,
      actionDetails: details,
      locationData: apiLog?.ip_address
        ? {
            ipAddress: apiLog.ip_address,
          }
        : undefined,
    };
  }

  /**
   * Get audit logs from backend
   */
  public async getAuditLogs(params: AuditLogSearchParams = {}): Promise<AuditLogResponse> {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 50;
    const filtersPayload = this.buildFiltersPayload(params);

    const queryParams = new URLSearchParams();
    queryParams.set('page', String(page));
    queryParams.set('page_size', String(pageSize));
    queryParams.set('filters', JSON.stringify(filtersPayload));
    if (params.searchTerm) {
      queryParams.set('search_term', params.searchTerm);
    }

    if (params.startDate) {
      queryParams.set('start_date', params.startDate);
    }
    if (params.endDate) {
      queryParams.set('end_date', params.endDate);
    }

    const token = getValidAccessToken();

    const response = await fetch(`${AUDIT_LOGS_API_URL}?${queryParams.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Audit log fetch failed with status ${response.status}`);
    }

    const data = await response.json();
    const logs = Array.isArray(data.logs) ? data.logs.map((log: any) => this.mapApiLogToEntry(log)) : [];

    return {
      success: true,
      logs,
      total: data.total ?? logs.length,
      page: data.page ?? page,
      pageSize: data.page_size ?? pageSize,
    };
  }

  /**
   * Export audit logs using the same API as the table (client-side CSV)
   */
  public async exportAuditLogs(params: AuditLogExportParams): Promise<Blob> {
    if (params.format !== 'csv') {
      throw new Error('Only CSV export is supported at this time.');
    }

    const sanitizedFilters = this.sanitizeFilters(params.filters);
    const exportParams: AuditLogSearchParams = {
      ...sanitizedFilters,
      startDate: params.startDate || sanitizedFilters.startDate,
      endDate: params.endDate || sanitizedFilters.endDate,
      sortBy: sanitizedFilters.sortBy || 'timestamp',
      sortOrder: sanitizedFilters.sortOrder || 'desc',
      page: 1,
      pageSize: sanitizedFilters.pageSize || 500,
    };

    const allLogs: AuditLogEntry[] = [];
    let hasMore = true;

    while (hasMore) {
      const response = await this.getAuditLogs(exportParams);
      allLogs.push(...response.logs);

      const total = response.total || allLogs.length;
      const fetched = allLogs.length;
      const pageSize = response.pageSize || exportParams.pageSize || 50;
      if (fetched >= total || response.logs.length < pageSize) {
        hasMore = false;
      } else {
        exportParams.page = (exportParams.page || 1) + 1;
      }
    }

    const csv = this.buildCsvFromLogs(allLogs);
    return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  }

  /**
   * Get user audit trail (placeholder)
   */
  public async getUserAuditTrail(userId: number): Promise<UserAuditTrail> {
    if (AUDIT_DEBUG_MODE) {
      console.log('👤 Fetching user audit trail (PLACEHOLDER):', userId);
    }

    // PLACEHOLDER: Return mock data
    return {
      userId,
      userEmail: 'placeholder@example.com',
      userName: 'Placeholder User',
      logs: [],
      summary: {
        totalActions: 0,
        lastActivity: new Date().toISOString(),
        frequentActions: [],
      },
    };

    // TODO: Implement actual backend call
  }

  /**
   * Get audit summary statistics (placeholder)
   */
  public async getAuditSummary(): Promise<AuditSummaryStats> {
    if (AUDIT_DEBUG_MODE) {
      console.log('📊 Fetching audit summary (PLACEHOLDER)');
    }

    // PLACEHOLDER: Return mock data
    return {
      totalLogs: 0,
      successfulActions: 0,
      failedActions: 0,
      uniqueUsers: 0,
      highRiskActions: 0,
      topActions: [],
      topResources: [],
      recentActivity: [],
    };

    // TODO: Implement actual backend call
  }

  /**
   * Flush queued logApiCallToAuditLog payloads as one api_logs bulk POST.
   */
  private async flushApiCallQueue(): Promise<void> {
    if (this.apiCallQueue.length === 0) return;

    const logsToSend = [...this.apiCallQueue];
    this.apiCallQueue = [];

    if (this.apiCallBatchTimer) {
      clearTimeout(this.apiCallBatchTimer);
      this.apiCallBatchTimer = null;
    }

    if (typeof window !== 'undefined') {
      const path = window.location.pathname || '';
      if (
        path.includes('/signup-request') ||
        path.includes('/campaign-60-day-pilot-A') ||
        path.includes('/Microsoft-Supported-60-Day-Compliance-Pilot')
      ) {
        return;
      }
    }

    if (AUDIT_DEBUG_MODE) {
      console.log('📝 API audit logs (PLACEHOLDER - Not sent):', logsToSend);
      return;
    }

    if (!this.isAuthenticated()) {
      this.apiCallQueue.unshift(...logsToSend);
      return;
    }

    const apiLogs = logsToSend.map((payload) => this.buildApiCallAuditBody(payload));

    try {
      const response = await fetch(AUDIT_LOGS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getValidAccessToken() || ''}`,
        },
        body: JSON.stringify({ api_logs: apiLogs }),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 422) {
          if (AUDIT_DEBUG_MODE) {
            console.warn(`⚠️ API audit bulk returned ${response.status}`);
          }
          return;
        }
        const errorText = await response.text();
        console.error('❌ Failed to send API audit log batch:', response.status, errorText);
      } else if (AUDIT_DEBUG_MODE) {
        console.log('✅ API audit log batch sent successfully');
      }
    } catch (error) {
      console.error('❌ Error sending API audit log batch:', error);
    }
  }

  private buildApiCallAuditBody(payload: ApiCallAuditLogPayload): Record<string, unknown> {
    const statusCode = payload.status_code ?? 200;
    const inferredActionType =
      payload.action_type ||
      (payload.method === 'GET'
        ? 'VIEW'
        : payload.method === 'POST'
          ? 'CREATE'
          : payload.method === 'PUT' || payload.method === 'PATCH'
            ? 'UPDATE'
            : payload.method === 'DELETE'
              ? 'DELETE'
              : 'VIEW');
    const inferredActionStatus =
      payload.action_status ||
      (statusCode >= 200 && statusCode < 300 ? 'SUCCESS' : 'FAILURE');

    return {
      user_id: payload.user_id,
      endpoint: payload.endpoint,
      method: payload.method,
      request_payload: payload.request_payload ?? {},
      response_payload: payload.response_payload ?? {},
      status_code: statusCode,
      error_message: payload.error_message ?? null,
      duration_ms: payload.duration_ms ?? 0,
      facility_info: payload.facility_info ?? {},
      before_value: payload.before_value ?? null,
      after_value: payload.after_value ?? null,
      action_details: payload.action_details ?? {},
      resource_type: payload.resource_type,
      action_type: inferredActionType,
      action_status: inferredActionStatus,
    };
  }

  private queueApiCallLog(payload: ApiCallAuditLogPayload): void {
    this.apiCallQueue.push(payload);

    if (this.apiCallQueue.length >= AUDIT_LOG_BATCH_SIZE) {
      void this.flushApiCallQueue();
      return;
    }

    if (!this.apiCallBatchTimer) {
      this.apiCallBatchTimer = setTimeout(() => {
        void this.flushApiCallQueue();
      }, AUDIT_LOG_BATCH_TIMEOUT);
    }
  }

  /**
   * Force flush all pending logs (useful on page unload)
   */
  public async flush(): Promise<void> {
    await Promise.all([this.flushQueue(), this.flushApiCallQueue()]);
  }

  /**
   * Update session ID (useful after login)
   * Also flushes any queued logs that were waiting for authentication
   */
  public refreshSession(): void {
    this.sessionId = randomId();
    sessionStorage.setItem(SESSION_ID_KEY, this.sessionId);
    
    // Now that user is authenticated, flush any queued logs
    if ((this.logQueue.length > 0 || this.apiCallQueue.length > 0) && this.isAuthenticated()) {
      // Use setTimeout to avoid blocking the login flow
      setTimeout(() => {
        void this.flush();
      }, 100);
    }
  }

  /**
   * Log API call to audit log endpoint
   * POST /api/v1/audit/log
   */
  public async logApiCallToAuditLog(payload: ApiCallAuditLogPayload): Promise<void> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      if (AUDIT_DEBUG_MODE) {
        console.log('⏭️ Audit: skipping logApiCallToAuditLog - no auth token present');
      }
      return;
    }

    this.queueApiCallLog(payload);
  }
}

// Export singleton instance
export const auditLogService = new AuditLogService();

// Export class for testing
export default AuditLogService;
